import React from 'react';
import { AlertTriangle, XCircle, UserCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Button from './ui/Button';

/**
 * Banner shown while an admin is viewing the app as another user.
 *
 * SECURITY (issue #19): this is a **view-as** mode, not a permissions test.
 * Impersonation is server-verified and audited, and the user object is the
 * target's real staff row — but the session JWT is still the admin's, so RLS
 * evaluates as the ADMIN for every read and write.
 *
 * That distinction has to be on screen. The banner previously said only
 * "Impersonating: <name> (<role>)", which reads as "you are now seeing what
 * they see". An admin checking whether a teacher can reach something would have
 * got the wrong answer and believed it — the exact harm #19 describes. Full
 * RLS-level enforcement is a separate, larger change; until it lands, saying so
 * is the mitigation.
 */
const ImpersonationBanner: React.FC = () => {
  const { user, isImpersonating, stopImpersonation } = useAuth();
  
  if (!isImpersonating || !user?.originalUser) {
    return null;
  }

  return (
    <div className="bg-navy-600 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-1 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <div className="flex items-center space-x-3">
            <UserCheck className="h-5 w-5" />
            <div className="flex items-center space-x-2">
              <span className="font-medium">Impersonating:</span>
              <span className="font-bold">{user.name || user.email}</span>
              <span className="text-navy-200">({user.role})</span>
            </div>
            <div className="hidden md:flex items-center space-x-2 ml-4 pl-4 border-l border-navy-500">
              <span className="text-navy-200">Original user:</span>
              <span>{user.originalUser.name || user.originalUser.email}</span>
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={stopImpersonation}
            className="bg-white text-navy-600 hover:bg-navy-50 self-start sm:self-auto flex-shrink-0"
          >
            <XCircle className="h-4 w-4 mr-1" />
            Exit Impersonation
          </Button>
        </div>

        {/* The limitation, stated plainly rather than left to be assumed. */}
        <div className="flex items-start gap-2 pb-2 text-xs text-navy-100">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-px" />
          <span>
            This shows their <strong>view</strong>, not their <strong>permissions</strong> —
            data is still read with your own admin access. Do not use this to check what
            someone can or cannot reach.
          </span>
        </div>
      </div>
    </div>
  );
};

export default ImpersonationBanner;